import moment from "moment-timezone";
import { ExcelComposer, SectionConfig } from "../excel/composer";
import * as Excel from "../excel";
import { CellFormat } from "../excel/common";
import { downloadBinaryData } from "../browser/download";
import { CommonDestinationTimezone } from "../utils";
import { SalesExportTracker, SalesExportTrackerContract } from "../api/exportSalesTracking";
import { formatNumberToDisplay } from "../api/common";
import { CommodityOptions } from "../api/commodity";

interface GroupedSettlement {
  tracker_id: string;
  grouped_id: string;
  customer_name: string;
  commodity: string;
  amount: number | string;
  grouped_date: string;
  contracts: SalesExportTrackerContract[];
  signed_count: number;
  contracts_count: number;
}

interface Filters {
    filterToOnlyUnsigned?: boolean
}

export async function generateExportSalesTrackingReport(
  trackers: SalesExportTracker[],
  filters: Filters
) {
  // Group data by settlement (grouped_id)
  const groupedData = groupTrackersBySettlement(trackers, filters);
  
  // Create Excel composer
  const composer = new ExcelComposer();
  composer.createSheet(moment.tz(CommonDestinationTimezone).format("YYYY-MM-DD"));
  
  // Add headers and data
  const section = composer.addSection(createReportSection(groupedData), {
    startRow: 1,
    startColumn: 1
  });
  
  // Generate filename with date
  const dateStr = moment.tz(CommonDestinationTimezone).format("MM-DD-YYYY");
  const fileName = `Export_Sales_Tracking_Report_${dateStr}.xlsx`;
  
  // Get buffer and download
  const buffer = await composer.getBuffer();
  downloadBinaryData(
    buffer,
    fileName,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
}

function groupTrackersBySettlement(trackers: SalesExportTracker[], filters: Filters): GroupedSettlement[] {
  const grouped: Record<string, GroupedSettlement> = {};
  
  trackers.forEach(tracker => {
    if (!grouped[tracker.grouped_id]) {
      grouped[tracker.grouped_id] = {
        tracker_id: tracker.id,
        grouped_id: tracker.grouped_id,
        customer_name: tracker.Customer?.long_name || "",
        commodity: tracker.commodity,
        amount: tracker.amount,
        grouped_date: tracker.grouped_date,
        contracts: tracker.Contracts || [],
        signed_count: tracker.Contracts?.filter(c => c.has_been_signed).length || 0,
        contracts_count: tracker.Contracts?.length || 0
      };
    }
  });
  
  return Object.values(grouped).filter(item => {
      let allow = true;
      if (filters.filterToOnlyUnsigned && (item.signed_count >= item.contracts_count )) {
          allow = false;
      }

      return allow;
  }).sort((a,b) => {
      const customerCompare = (a["customer_name"] || "").localeCompare(b["customer_name"] || "");
      if (customerCompare !== 0) return customerCompare;

    const aDate = moment.tz(a.grouped_date, CommonDestinationTimezone);
    const bDate = moment.tz(b.grouped_date, CommonDestinationTimezone);
    const dateComparison = bDate.valueOf() - aDate.valueOf();
    return dateComparison !== 0 ? dateComparison : 0;
  });
}

function createReportSection(groupedData: GroupedSettlement[]): SectionConfig {
  const headers = [
    "Customer",
    "Commodity",
    "S/A/V No",
    "Contract",
    "Destination CTR",
    "Qualifying Sales Contract Signed",
    "S/A/V Date",
    "Amount USD",
    "Notes",
  ];

  const data = groupedData.map(settlement => {
    // Sort contracts: signed first, then unsigned
    const sortedContracts = [...settlement.contracts].sort((a, b) => {
      if (a.has_been_signed === b.has_been_signed) return 0;
      return a.has_been_signed ? -1 : 1;
    });
    
    // Create contract numbers string
    const contractNumbers = sortedContracts
      .map(c => c.contract_number)
      .join(", ");
    
    // Create signed status string (x for signed, empty for unsigned)
    const signedStatus = sortedContracts
      .map(c => c.has_been_signed ? "x" : "")
      .filter(Boolean)
      .join(",");
    
    // Parse amount as number for Excel
    const amountValue = typeof settlement.amount === 'string' 
      ? Excel.safeParseDecimal(settlement.amount).toNumber()
      : settlement.amount;

    return [
      { value: settlement.customer_name },
      { value: settlement.commodity },
      { value: settlement.grouped_id },
      { value: "" },
      { value: contractNumbers },
      { value: signedStatus },
      { value: settlement.grouped_date ? moment.tz(settlement.grouped_date, CommonDestinationTimezone).format("MM/DD/YYYY") : "" },
      { value: amountValue, format: CellFormat.ACCOUNTING },
      { value: "" },
    ];
  });
  
  return {
    headers,
    data,
    footerCalculations: [
      { type: "value", value: "Total:", column: 6, bold: true },
      { type: "sum", column: 7, bold: true, format: CellFormat.ACCOUNTING }
    ],
    styling: {
      columns: {
        A: { width: 35 },
        B: { width: 10 },
        C: { width: 15 },
        D: { width: 20 },
        E: { width: 35 },
        F: { width: 30 },
        G: { width: 20 },
        H: { width: 25 },
        I: { width: 15 },
      },
      header: {
        bold: true,
        border: { type: "bottom" }
      },
    }
  };
}
